import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Sticky right-side sidebar to toggle metric visibility.
 *
 * Props:
 *  - metrics        – Array of { key, label, color, icon } objects (in display order)
 *  - visibleMetrics – Set<string> of currently visible metric keys
 *  - onChange        – (nextSet: Set<string>) => void
 *  - allKeys        – string[] – all metric keys (used for "All" button, defaults to metrics.map(m=>m.key))
 *  - storageKey      – sessionStorage key for sidebar open/closed (e.g. "internalLoad_sidebarOpen")
 *  - isDarkMode
 */
export default function MetricVisibilitySidebar({
  metrics,
  visibleMetrics,
  onChange,
  allKeys,
  storageKey,
  isDarkMode = false,
}) {
  const { t } = useTranslation();
  const keys = allKeys ?? metrics.map((m) => m.key);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === "true";
    } catch {}
    return false;
  });

  const toggle = () =>
    setSidebarOpen((v) => {
      const next = !v;
      try { sessionStorage.setItem(storageKey, String(next)); } catch {}
      return next;
    });

  const setAll = () => onChange(new Set(keys));
  const setNone = () => onChange(new Set());

  const toggleKey = (key) =>
    onChange(
      ((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      })(visibleMetrics)
    );

  return (
    <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex items-center">
      {/* Toggle arrow */}
      <button
        onClick={toggle}
        className={`w-6 h-32 flex items-center justify-center rounded-l-lg shadow-md transition-colors ${
          isDarkMode
            ? "bg-indigo-600 hover:bg-indigo-500 text-white"
            : "bg-indigo-500 hover:bg-indigo-400 text-white"
        }`}
        title={sidebarOpen ? t("external_load.hide_filters") : t("external_load.metrics")}
      >
        {sidebarOpen ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </button>

      {/* Sliding panel */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          sidebarOpen ? "w-52 opacity-100" : "w-0 opacity-0"
        }`}
      >
        <div
          className={`w-52 rounded-l-xl shadow-2xl border border-r-0 p-3 max-h-[70vh] overflow-y-auto ${
            isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
        >
          <div
            className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {t("external_load.metrics")}
          </div>

          <div className="flex gap-1.5 mb-3">
            <button
              onClick={setAll}
              className={`text-[10px] px-2 py-0.5 rounded border ${
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t("external_load.all")}
            </button>
            <button
              onClick={setNone}
              className={`text-[10px] px-2 py-0.5 rounded border ${
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t("external_load.none")}
            </button>
          </div>

          <div className="space-y-1">
            {metrics.map((m) => {
              const isOn = visibleMetrics.has(m.key);
              const Icon = m.icon;
              return (
                <label
                  key={m.key}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors text-xs ${
                    isOn
                      ? isDarkMode
                        ? "bg-gray-700/60"
                        : "bg-gray-50"
                      : isDarkMode
                      ? "hover:bg-gray-700/30"
                      : "hover:bg-gray-50/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={() => toggleKey(m.key)}
                    className="sr-only"
                  />
                  <span
                    className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${
                      isOn
                        ? "border-transparent text-white"
                        : isDarkMode
                        ? "border-gray-500"
                        : "border-gray-300"
                    }`}
                    style={isOn ? { background: m.color } : {}}
                  >
                    {isOn && <Check className="h-3 w-3" />}
                  </span>
                  {Icon && (
                    <Icon
                      className="h-3.5 w-3.5 flex-shrink-0"
                      style={{ color: m.color }}
                    />
                  )}
                  <span className={isDarkMode ? "text-gray-200" : "text-gray-700"}>
                    {m.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
